import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import { Button } from '@progress/kendo-react-buttons';
import { AxiosError } from 'axios';
import { useEnums, useGlobalContext, useToasts } from 'Context';
import { AsyncStatus } from 'Models';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { ADD_EDIT } from 'Navigation';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import LabProfile from 'Assets/Icons/lab_profile_form.svg';
import Diagnosis from 'Assets/Icons/diagnosis_form.svg';
import DataInfo from 'Assets/Icons/data_info_alert_form.svg';
import {
  buildEndpoint,
  defaultDataTransform,
  DraftStatus,
  EMPTY_GUID,
  SearchParams,
  RequestStatus,
  useApiWorker,
  useDataFetcher,
  useDataPoster,
  useIdParam,
} from 'Utilities';
import { formatDate } from 'v3/Components/Form/DateTimePickers/DateTimePicker';
import { formatDateToUtc, parseUtcToLocal } from 'Utilities/DateTimeUtc';
import dayjs from 'dayjs';
import FormErrorSection from 'v3/Components/Form/FormErrorSection';
import FormSection, { FieldError } from 'v3/Components/Form/FormSection';
import { PrimaryButton, SecondaryButton } from 'v3/Components/Shared/Buttons';
import { ConfirmDialog } from 'v3/Components/Shared/ConfirmDialog';
import { LineOfBusinessEnum } from 'v3/Models/Enums';
import { ReferralsRequest } from 'v3/Models/ReferralRequest';
import {
  AdditionalInformationActive,
  AdditionalInformationReadOnly,
} from './AddEditReferralsRequest.AdditionalInformation';
import {
  AttorneyInformationActive,
  AttorneyInformationReadOnly,
} from './AddEditReferralsRequest.AttorneyInformation';
import {
  ClaimantInformationActive,
  ClaimantInformationReadOnly,
} from './AddEditReferralsRequest.ClaimantInformation';
import {
  ClaimDetailsActive,
  ClaimDetailsReadOnly,
} from './AddEditReferralsRequest.ClaimDetails';
import AddEditReferralsRequestFooter from './AddEditReferralsRequest.Footer';
import './AddEditReferralsRequest.Footer.scss';
import AddEditReferralsRequestHeader from './AddEditReferralsRequest.Header';
import {
  ReportDetailsActive,
  ReportDetailsReadOnly,
} from './AddEditReferralsRequest.ReportDetails';
import {
  ReportQuestionsActive,
  ReportQuestionsReadOnly,
} from './AddEditReferralsRequest.ReportQuestions';
import './AddEditReferralsRequest.scss';
import {
  createReferralRequestSchema,
  DEFAULT_REFERRAL_REQUEST,
  useReferralRequestForm,
} from './useReferralRequestForm';
import { DENIED_OWNER_SESSION_KEY } from 'Features/Referrals/ReferralOwnershipModal';

const Sections = {
  ClaimantInformation: 'Claimant Information',
  ClaimDetails: 'Claim Details',
  AttorneyDetails: 'Attorney Details',
  ReportDetails: 'Report Details',
  ReportQuestions: 'Report Questions',
  MedicalRecords: 'Medical Records',
};

const AddEditReferralsRequest: React.FC = () => {
  const API = useApiWorker('3.0');
  const toast = useToasts();
  const globalContext = useGlobalContext();
  const [activeSection, setActiveSection] = useState(
    Sections.ClaimantInformation
  );
  const userdata = globalContext[0]?.currentUser;
  const isInternal = userdata?.isInternal;
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const lob = searchParams.get(SearchParams.LOB);
  const isDraft = searchParams.get(SearchParams.DRAFT) === '1';
  const clientId = searchParams.get('clientId');
  const referrerId = searchParams.get('referrerId');
  const otherReferrerName = searchParams.get('otherReferrerName');

  const { lineOfBusiness, referralsRequestDraftStatus } = useEnums();
  const ReferralRequestSchema = useMemo(
    () => createReferralRequestSchema(Number(lob) as LineOfBusinessEnum),
    [lob]
  );

  const currentLineOfBusiness = Object.values(lineOfBusiness).find(
    (item) => item.value === Number(lob)
  );
  function isSectionCompleted(
    section: string,
    values: Record<string, any>,
    schema = ReferralRequestSchema
  ): boolean {
    const fields = Object.entries(schema.fields)
      .filter(
        ([_, field]: [string, any]) =>
          field.describe().meta?.required &&
          field.describe().meta?.section === section
      )
      .map(([key]) => key);
    return fields.every((field) => {
      const value = values[field];
      return value !== undefined && value !== null && value !== '';
    });
  }

  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);
  const [sectionsWithErrors, setSectionsWithErrors] = useState<string[]>([]);

  let history = useHistory();

  const [id, isAdd] = useIdParam();

  // Clear any stored ownership denial so the modal reappears on the next referral visit.
  useEffect(() => {
    sessionStorage.removeItem(DENIED_OWNER_SESSION_KEY);
  }, []);

  const [isFirstEdit, setIsFirstEdit] = useState(!isAdd);
  const [referralRequest, loadingStatus] = useDataFetcher<ReferralsRequest>(
    buildEndpoint(
      id !== EMPTY_GUID
        ? isDraft
          ? ENDPOINTS.referralsRequestsDraft.base
          : 'ReferralsRequest/'
        : ENDPOINTS.referralsRequestsDraft.base,
      id,
      ADD_EDIT
    ),
    DEFAULT_REFERRAL_REQUEST,
    defaultDataTransform,
    ReferralsRequest,
    true,
    '3.0'
  );

  const handleResponse = (response: any) => {
    if (response.status >= 200 && response.status < 300) {
      toast.success('The Referral Request has been updated');
    } else {
      toast.error('Something went wrong. Please try again');
      console.log(response);
    }
  };

  const handleError = (error: AxiosError) => {
    if (error.response?.status === 400) {
      toast.error('Please validate the highlighted fields');

      const errors = Object.entries(error.response?.data.errors).map(
        ([key, value]) => ({
          fieldId: key,
          error: (value as string[])[0],
        })
      );

      setFieldErrors(errors);

      let sections = errors
        .map((e) => {
          let id = e.fieldId;
          if (id.includes('[')) {
            id = id.split('[')[0];
          }

          const fieldDescription = ReferralRequestSchema.fields[id].describe();
          return fieldDescription.meta.section;
        })
        .reduce((errors, item) => {
          if (!errors.includes(item)) {
            errors.push(item);
          }
          return errors;
        }, []);

      setSectionsWithErrors(sections);
    }
  };

  const [postDraftData, { postStatus: postDraftDataStatus }] = useDataPoster(
    buildEndpoint(ENDPOINTS.referralsRequestsDraft.base),
    handleResponse,
    false,
    '3.0',
    handleError
  );

  const [postReferralRequestData, { postStatus: postRequestDataStatus }] =
    useDataPoster(
      buildEndpoint('/ReferralsRequest'),
      handleResponse,
      false,
      '3.0',
      handleError
    );

  /**
   * "Number of Reviewer Specialties" pre-fills one placeholder slot per
   * specialty, and any slot the user never picks from stays
   * `{ value: null, displayName: '' }`. Those placeholders bind to nulls
   * server-side and end up stored as `[null]`, which breaks the CSV exports
   * (STRAD2-2637). Returns the payload with the unfilled slots dropped, leaving
   * the form state untouched so the dropdowns keep their positions if the
   * request fails.
   */
  function toPayload(referralRequestForm: any) {
    const values = referralRequestForm.values;
    const specialties = values['requestedReviewerSpecialties'];
    if (!Array.isArray(specialties)) return values;
    return {
      ...values,
      requestedReviewerSpecialties: specialties.filter(
        (specialty: any) =>
          specialty !== null &&
          specialty !== undefined &&
          specialty.value !== null &&
          specialty.value !== undefined
      ),
    };
  }

  function setFormInternalValues(referralRequestForm: any) {
    referralRequestForm.values['lineOfBusiness'] = currentLineOfBusiness;
    referralRequestForm.values['localDateTime'] = formatDateToUtc(new Date());
    referralRequestForm.values['clientId'] = clientId;
    referralRequestForm.values['referralSourceId'] =
      referrerId ?? referralRequestForm.values['referralSourceId'];
    referralRequestForm.values['otherReferrerName'] =
      referralRequestForm.values['otherReferrerName'] ?? otherReferrerName;
  }

  const saveDraft = async (referralRequestForm: any) => {
    if (isUploadingFiles) {
      toast.info(
        'Please wait until all files finish uploading before saving the draft.'
      );
      return;
    }

    referralRequestForm.values['skipValidation'] = true;
    referralRequestForm.values['draftStatus'] = Object.values(
      referralsRequestDraftStatus
    ).find((item) => item.displayName === DraftStatus.INPROGRESS);
    setFormInternalValues(referralRequestForm);
    const response = await postDraftData(toPayload(referralRequestForm));
    if (
      response &&
      typeof response === 'object' &&
      'status' in response &&
      (response as any).status === 200
    ) {
      goBack();
    }
  };

  function goBack() {
    userdata?.isClient
      ? history.push('/Referrals/DraftReferrals')
      : history.goBack();
  }

  const [toggleDeleteDialog, setToggleDeleteDialog] = useState(false);

  const discardDraft = async (referralRequestForm: any) => {
    if (id !== EMPTY_GUID) {
      let response = await API.delete(
        buildEndpoint(ENDPOINTS.referralsRequestsDraft.base, id)
      );
      if (response?.status === 200) {
        toast.success('The draft has been discarded');
      }
    }
    goBack();
  };

  const onSubmit = async (referralRequestForm: any) => {
    if (isUploadingFiles) {
      toast.info(
        'Please wait until all files finish uploading before saving the draft.'
      );
      return;
    }

    referralRequestForm.values['draftStatus'] = Object.values(
      referralsRequestDraftStatus
    ).find((item) => item.displayName === DraftStatus.SUBMITTED);
    setFormInternalValues(referralRequestForm);
    const response = await postDraftData(toPayload(referralRequestForm));

    if (
      response &&
      typeof response === 'object' &&
      'status' in response &&
      (response as any).status === 200
    ) {
      history.goBack();
    }
  };

  const onResubmit = async (referralRequestForm: any) => {
    if (isUploadingFiles) {
      toast.info(
        'Please wait until all files finish uploading before saving the draft.'
      );
      return;
    }

    referralRequestForm.values['localDateTime'] = formatDateToUtc(new Date());
    referralRequestForm.values['draftStatus'] = Object.values(
      referralsRequestDraftStatus
    ).find((item) => item.displayName === RequestStatus.SUBMITTED);
    referralRequestForm.values['lineOfBusiness'] = Object.values(
      lineOfBusiness
    ).find((item) => item.value === referralRequest.lineOfBusiness?.value);
    const response = await postReferralRequestData(
      toPayload(referralRequestForm)
    );

    if (
      response &&
      typeof response === 'object' &&
      'status' in response &&
      (response as any).status === 200
    ) {
      history.goBack();
    }
  };

  const form = useReferralRequestForm({
    onSubmit: onSubmit,
    referralRequest: referralRequest,
    isInitialLoad: isAdd,
  });

  const [reportQuestions, reportQuestionsStatus] = useDataFetcher<any>(
    buildEndpoint(
      `${
        ENDPOINTS.referralsRequestsDraft.base
      }/getReportQuestions?lob=${lob}&serviceId=${form.values.service?.id}
      &${clientId != null ? 'clientId=' + clientId : ''}`
    ),
    [],
    defaultDataTransform,
    undefined,
    form.getFieldProps('service').value?.id !== undefined,
    '3.0'
  );

  useEffect(() => {
    const updatedClientReportQuestions = reportQuestions.map(
      (question: any) => {
        const matchingQuestion = referralRequest.clientReportQuestions?.find(
          (q: any) => q.id === question.id
        );
        if (matchingQuestion) {
          question.isSelected = matchingQuestion.isSelected;
          return question;
        }
        return { ...question, isSelected: true };
      }
    );

    form.setFieldValue('clientReportQuestions', updatedClientReportQuestions);
  }, [reportQuestions, referralRequest]);

  function IsActive(sectionName: string): boolean | undefined {
    return !isFirstEdit && activeSection === sectionName;
  }

  const SetActive = (sectionName: string) => {
    setActiveSection(sectionName);
    setIsFirstEdit(false);
  };

  useEffect(() => {
    if (sectionsWithErrors.length > 0) {
      const id = 'form-validation-banner';
      const element = document.getElementById(id);
      element?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [sectionsWithErrors]);

  const setFieldError = (fieldId: string, error: string) => {
    setFieldErrors([...fieldErrors, { fieldId, error }]);
  };

  return loadingStatus === AsyncStatus.Success ? (
    <div>
      <AddEditReferralsRequestHeader
        onDiscard={() => {
          setToggleDeleteDialog(!toggleDeleteDialog);
        }}
        isDraft={isDraft}
        onSaveDraft={() => saveDraft(form)}
        savingDraft={postDraftDataStatus === AsyncStatus.Pending}
        lob={currentLineOfBusiness?.displayName}
        isUploadingFiles={isUploadingFiles}
        status={
          referralRequest.status?.displayName ??
          referralRequest?.draftStatus?.displayName
        }
        referralDate={
          referralRequest.referralDate
            ? dayjs(
                typeof referralRequest.referralDate === 'string'
                  ? parseUtcToLocal(referralRequest.referralDate)
                  : referralRequest.referralDate
              ).format('MMMM DD, YYYY hh:mma')
            : ''
        }
        dueDate={
          form.values.dueDate
            ? dayjs(
                typeof form.values.dueDate === 'string'
                  ? parseUtcToLocal(form.values.dueDate)
                  : form.values.dueDate
              ).format('MMMM DD, YYYY hh:mma')
            : ''
        }
      />
      <ConfirmDialog
        title="Are you sure you want to discard this referral draft?"
        toggleDialog={() => {
          setToggleDeleteDialog(!toggleDeleteDialog);
        }}
        dialogVisible={toggleDeleteDialog}
        primaryButtonText="Discard Draft"
        secondaryButtonText="Keep Draft"
        OnSecondaryButtonClick={() =>
          setToggleDeleteDialog(!toggleDeleteDialog)
        }
        OnPrimaryButtonClick={() => discardDraft(form)}
        isPrimaryDanger
      >
        You will lose all the information on this referral
      </ConfirmDialog>
      <div className="body">
        <p>
          Please provide complete and accurate patient, claim, and report
          information below. All required fields are mandatory for request
          submission.
        </p>
        {sectionsWithErrors.length > 0 && (
          <FormErrorSection
            sectionsWithErrors={sectionsWithErrors}
            onSectionClick={(section) => setActiveSection(section)}
          />
        )}
        <FormSection
          title={Sections.ClaimantInformation}
          activeContent={
            <ClaimantInformationActive form={form} fieldErrors={fieldErrors} />
          }
          readOnlyContent={<ClaimantInformationReadOnly form={form} />}
          isActive={IsActive(Sections.ClaimantInformation)}
          onActive={() => SetActive(Sections.ClaimantInformation)}
          initializedByDefault
          completed={isSectionCompleted(
            Sections.ClaimantInformation,
            form.values
          )}
        />
        <FormSection
          title={Sections.ClaimDetails}
          activeContent={
            <ClaimDetailsActive form={form} fieldErrors={fieldErrors} />
          }
          readOnlyContent={<ClaimDetailsReadOnly form={form} />}
          isActive={IsActive(Sections.ClaimDetails)}
          onActive={() => SetActive(Sections.ClaimDetails)}
          initialIcon={<img src={LabProfile} />}
          initialText="Add information about the claim details such as claim number and date of injury"
          initializedByDefault={!isAdd}
          completed={isSectionCompleted(Sections.ClaimDetails, form.values)}
        />
        <FormSection
          title={Sections.AttorneyDetails}
          activeContent={
            <AttorneyInformationActive form={form} fieldErrors={fieldErrors} />
          }
          readOnlyContent={
            <AttorneyInformationReadOnly
              form={form}
              fieldErrors={fieldErrors}
            />
          }
          isActive={IsActive(Sections.AttorneyDetails)}
          onActive={() => SetActive(Sections.AttorneyDetails)}
          initialIcon={<GavelOutlinedIcon />}
          initialText="Add information about the attorneys on the claim"
          initializedByDefault={!isAdd}
          completed={false}
          optional
        />
        <FormSection
          title={Sections.ReportDetails}
          activeContent={
            <ReportDetailsActive
              form={form}
              fieldErrors={fieldErrors}
              setFieldError={setFieldError}
            />
          }
          readOnlyContent={<ReportDetailsReadOnly form={form} />}
          isActive={IsActive(Sections.ReportDetails)}
          onActive={() => SetActive(Sections.ReportDetails)}
          initialIcon={<img src={Diagnosis} />}
          initialText="Add details about the report and such as the due date of the report and what requirements it has"
          initializedByDefault={!isAdd}
          completed={isSectionCompleted(
            Sections.ReportDetails,
            form.values,
            ReferralRequestSchema
          )}
        />
        <FormSection
          title={Sections.ReportQuestions}
          activeContent={
            <ReportQuestionsActive form={form} fieldErrors={fieldErrors} />
          }
          readOnlyContent={<ReportQuestionsReadOnly form={form} />}
          isActive={IsActive(Sections.ReportQuestions)}
          onActive={() => SetActive(Sections.ReportQuestions)}
          initialIcon={<ContactSupportOutlinedIcon />}
          initialContent={
            <div
              className={`initial-content ${
                form.getFieldProps('service').value?.id === undefined
                  ? 'disabled'
                  : ''
              }`}
            >
              <div className="icon-container">
                <ContactSupportOutlinedIcon />
              </div>
              <div>
                <p>
                  Select a service type in “Report Details” above in order to
                  view the corresponding questions for this referral
                </p>
                <div
                  className="link jump-to-service"
                  onClick={() => {
                    SetActive(Sections.ReportDetails);
                    const serviceSelection =
                      document.getElementById('reportdetails');
                    serviceSelection?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Jump to Service Selection
                </div>
              </div>
            </div>
          }
          disabled={form.getFieldProps('service').value?.id === undefined}
          initializedByDefault={
            !isAdd || form.getFieldProps('service').value?.id !== undefined
          }
          completed={false}
        />
        <FormSection
          title={Sections.MedicalRecords}
          activeContent={
            <AdditionalInformationActive
              form={form}
              fieldErrors={fieldErrors}
              onUploadStatusChange={setIsUploadingFiles}
            />
          }
          readOnlyContent={<AdditionalInformationReadOnly form={form} />}
          isActive={IsActive(Sections.MedicalRecords)}
          onActive={() => SetActive(Sections.MedicalRecords)}
          initialIcon={<img src={DataInfo} />}
          initialText="Add special instructions and the medical records tied to this claim"
          initializedByDefault={!isAdd}
          completed={false}
        />
        <div className="referral-footer">
          {referralRequest.status?.displayName === RequestStatus.PENDING ? (
            <div className="action-buttons">
              <SecondaryButton
                title="Cancel"
                className="centered no-border"
                onClick={() => history.goBack()}
              />
              <PrimaryButton
                className="centered"
                onClick={() => onResubmit(form)}
                disabled={
                  postRequestDataStatus === AsyncStatus.Pending ||
                  isUploadingFiles
                }
                title="Resubmit"
              />
            </div>
          ) : (
            <div className="footer-buttons">
              <AddEditReferralsRequestFooter
                onDiscard={() => {
                  setToggleDeleteDialog(!toggleDeleteDialog);
                }}
                onSaveDraft={() => saveDraft(form)}
                savingDraft={postDraftDataStatus === AsyncStatus.Pending}
                lob={currentLineOfBusiness?.displayName}
                showDiscardButton={false}
                isUploadingFiles={isUploadingFiles}
              />
              <PrimaryButton
                className="centered"
                onClick={() => onSubmit(form)}
                disabled={
                  postDraftDataStatus === AsyncStatus.Pending ||
                  isUploadingFiles
                }
                title="Submit Referral"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
};

export default AddEditReferralsRequest;
